import { Clipboard } from '@angular/cdk/clipboard';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { CardManagementService } from '@app/finxone-web-frontend/app/lib/services/card-management-service/card-management-service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CardState } from '@app/finxone-web-frontend/app/lib/state/card.state';
import { findAndShowAlertFromCode } from '@app/finxone-web-frontend/app/lib/utils/alert-code-utils/alert-code.utils';
import { formatDateMMYY, formatPAN } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { CardsSuccessCodes } from '@app/translations';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  BaseCssProperties,
  CardInfoType,
  CardSensitiveDetailsSession,
  CardsWidgetConfig,
  ProviderClass,
  SecureCardInfoResponse,
  baseCssConfigStyle,
  commonContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, catchError, map, switchMap, take, takeUntil, throwError } from 'rxjs';
import { BaseComponent } from '../../base/base.component';
import { CardSensitiveDetailsRendererService } from '../../../../../lib/services/card-management-service/card-sensitive-details-renderer.service';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-card-sensitive-details',
  templateUrl: './card-sensitive-details.component.html',
})
export class CardSensitiveDetailsComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  private confirmation: string;
  private currentCardDetail: CardInfoType;
  public cardType: string;
  public secureCVVDetails: SecureCardInfoResponse = { pan: '', cvv2: '', expDate: '' };
  public sensitiveDetailsSession?: CardSensitiveDetailsSession;
  public iconStyle: baseCssConfigStyle;
  public copyIcon: string;
  public labelTextColor: string;
  public detailsTextColor: string;
  public panCopyTemplate: string;
  public expiryCopyTemplate: string;
  public cvv2CopyTemplate: string;
  public isWorkflow: boolean;
  public providerSdkError?: string;
  public providerSdkLoading = false;
  public panSdkDomId: string;
  public expSdkDomId: string;
  public cvvSdkDomId: string;
  public copyPanSdkDomId: string;
  public copyExpSdkDomId: string;
  public copyCvvSdkDomId: string;
  public airwallexIframeHostId: string;
  readonly CardsSuccessCodes = CardsSuccessCodes;

  constructor(
    public dataIn: ProviderClass<CardsWidgetConfig>,
    private cardManagementService: CardManagementService,
    private cardSensitiveDetailsRendererService: CardSensitiveDetailsRendererService,
    private store: Store,
    private configService: ConfigService,
    private alertService: AlertHandlerService,
    private clipboard: Clipboard,
    private activatedRoute: ActivatedRoute,
  ) {
    super();
    this.currentCardDetail = this.store.selectSnapshot(CardState.getCurrentCardDetail);
  }

  ngOnInit() {
    this.handleParamsAndFetchDetails();
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
    this.cardType =
      this.currentCardDetail.type.charAt(0).toUpperCase() + this.currentCardDetail.type.slice(1) + ' Card';
    this.initialiseProviderSdkDomIds();

    if (this.isWorkflow) {
      this.secureCVVDetails = {
        pan: formatPAN('1234567891234567'),
        cvv2: '123',
        expDate: '11/25',
        pin: '9876',
      };
      this.sensitiveDetailsSession = {
        provider: 'mock',
        mode: 'backend-values',
        secureDetails: {
          pan: '1234567891234567',
          cvv2: '123',
          expDate: '11/25',
        },
      };
    }

    this.copyIcon = this.dataIn.attri?.widgetProperties?.icon ?? '';

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      ...commonContentStyleList,
      BaseCssProperties.TEXT_FONT_STYLE,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
    ]);
    this.iconStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.ICON_COLOR,
      BaseCssProperties.ICON_SIZE,
    ]);

    this.labelTextColor = this.dataIn.attri.widgetProperties['labelTextColor'] ?? 'var(--color-on-background-muted)';
    this.detailsTextColor =
      this.dataIn.attri.widgetProperties['detailsTextColor'] ?? 'var(--color-on-background)';
  }

  ngAfterViewInit(): void {
    this.tryRenderProviderSdkSession();
  }

  ngOnDestroy(): void {
    void this.cardSensitiveDetailsRendererService.teardown();
  }

  public copyContent(copyContent: string, alertEnum: CardsSuccessCodes) {
    // strip out any spaces or slashes
    const textContent = copyContent.replace(/[\s/]/g, '');
    this.clipboard.copy(textContent);

    findAndShowAlertFromCode(this.store, this.parentRouter, this.alertService, [alertEnum]);
  }

  public isDataFetching(secureDetails: SecureCardInfoResponse): boolean {
    return !this.sensitiveDetailsSession && secureDetails.cvv2 === '' && secureDetails.pan === '' && !this.providerSdkError;
  }

  public isBackendValuesMode(): boolean {
    return this.sensitiveDetailsSession?.mode === 'backend-values';
  }

  public isProviderSdkMode(): boolean {
    return this.sensitiveDetailsSession?.mode === 'provider-sdk' && !this.isAirwallexIframeMode();
  }

  public isAirwallexIframeMode(): boolean {
    return (
      this.sensitiveDetailsSession?.mode === 'provider-sdk' &&
      this.sensitiveDetailsSession.providerSession?.sdk === 'airwallex'
    );
  }

  private handleParamsAndFetchDetails(): void {
    if (this.activatedRoute?.queryParams) {
      this.activatedRoute.queryParams
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          switchMap((paramMap: Params) => {
            this.confirmation = paramMap?.['confirmation'];
            if (!this.confirmation) {
              return throwError(() => new Error('No confirmation token provided'));
            }
            this.providerSdkError = undefined;
            this.providerSdkLoading = true;
            return this.cardManagementService.getSensitiveDetailsSession(
              {
                cardId: this.currentCardDetail.id,
              },
              this.confirmation,
            );
          }),
          map((response: CardSensitiveDetailsSession) => {
            this.sensitiveDetailsSession = response;

            if (response.mode === 'backend-values') {
              this.secureCVVDetails = {
                pan: formatPAN(response.secureDetails?.pan ?? ''),
                cvv2: response.secureDetails?.cvv2 ?? '',
                expDate: response.secureDetails?.expDate || formatDateMMYY(this.currentCardDetail.expiry),
              };

              this.panCopyTemplate = `${this.secureCVVDetails.pan}`;
              this.cvv2CopyTemplate = `${this.secureCVVDetails.cvv2}`;
              this.expiryCopyTemplate = `${this.secureCVVDetails.expDate}`;
            } else {
              this.secureCVVDetails = {
                pan: '',
                cvv2: '',
                expDate: response.secureDetails?.expDate || formatDateMMYY(this.currentCardDetail.expiry),
              };
              setTimeout(() => {
                this.tryRenderProviderSdkSession();
              });
            }
            this.providerSdkLoading = false;
          }),
          catchError((error) => {
            console.error('Error fetching CVV details:', error);
            this.providerSdkLoading = false;
            this.providerSdkError = 'Unable to load secure card details.';
            return throwError(() => error);
          }),
        )
        .subscribe();
    }
  }

  private tryRenderProviderSdkSession(): void {
    if (this.sensitiveDetailsSession?.mode !== 'provider-sdk') {
      return;
    }

    this.providerSdkLoading = true;
    void this.cardSensitiveDetailsRendererService
      .renderSensitiveDetails(this.sensitiveDetailsSession, {
        panDomId: this.panSdkDomId,
        expDomId: this.expSdkDomId,
        cvvDomId: this.cvvSdkDomId,
        copyPanDomId: this.copyPanSdkDomId,
        copyExpDomId: this.copyExpSdkDomId,
        copyCvvDomId: this.copyCvvSdkDomId,
        iframeHostId: this.airwallexIframeHostId,
      }, {
        onCopyPanSuccess: () => this.showCopySuccess(CardsSuccessCodes.CARD_NUMBER_COPIED),
        onCopyExpSuccess: () => this.showCopySuccess(CardsSuccessCodes.CARD_EXP_DATE_COPIED),
        onCopyCvvSuccess: () => this.showCopySuccess(CardsSuccessCodes.CARD_CVV_COPIED),
        onFailure: (error: unknown) => {
          console.error('Marqeta.js callback failure:', error);
        },
      })
      .then(() => {
        this.providerSdkLoading = false;
      })
      .catch((error: unknown) => {
        console.error('Error rendering provider SDK sensitive details:', error);
        this.providerSdkLoading = false;
        this.providerSdkError = 'Secure card details could not be rendered on this device.';
      });
  }

  private initialiseProviderSdkDomIds(): void {
    const suffix = (this.currentCardDetail.id ?? 'card').replace(/[^a-zA-Z0-9_-]/g, '');
    this.panSdkDomId = `mq-card-pan-${suffix}`;
    this.expSdkDomId = `mq-card-exp-${suffix}`;
    this.cvvSdkDomId = `mq-card-cvv-${suffix}`;
    this.copyPanSdkDomId = `mq-copy-card-pan-${suffix}`;
    this.copyExpSdkDomId = `mq-copy-card-exp-${suffix}`;
    this.copyCvvSdkDomId = `mq-copy-card-cvv-${suffix}`;
    this.airwallexIframeHostId = `awx-card-details-${suffix}`;
  }

  private showCopySuccess(alertEnum: CardsSuccessCodes): void {
    findAndShowAlertFromCode(this.store, this.parentRouter, this.alertService, [alertEnum]);
  }
}
